import { Flex } from 'app/components/common/Flex';
import { Col, Radio, Row, Select, Space } from 'antd';
import { LoadingOutlined, SettingFilled } from '@ant-design/icons';
import React, { PureComponent } from 'react';
import {
    CapacityModel,
    CountType,
    FlightId,
    MonitoredLoadValue,
    MonitoredOccupancyLoadValues,
    TimePeriod,
    TrafficType,
} from 'app/model/CORE';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { CountsGraph } from '../graph/CountsGraph';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import memoizeOne from 'memoize-one';
import { RegulationPeriods } from 'app/model/client/CORE';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import { ExtendedMonitoredTv, TimeRangeCounts } from 'app/model/SOL49';
import CapacityUtils from 'app/common/util/CapacityUtils';
import AppContext from 'app/common/context/AppContext';

const DEFAULT_OTMV_DURATION = 1;

interface TvDetailsComparisonProps {
    currentTime: Date;
    timePeriod: TimePeriod;
    tvId: string;
    tvSet: string;
    regulationPeriods?: Array<RegulationPeriods>;
    proposalRegulationPeriods?: Array<RegulationPeriods>;
    occupancyPeriods?: Map<FlightId, TimePeriod>;
    onManualRefresh?: () => void;

    monitoredTvAfter: ExtendedMonitoredTv;
    monitoredTvBefore: ExtendedMonitoredTv;
}

interface TvDetailsComparisonState {
    display: CountType;

    // Internal state
    timeOver: Date;
    entryInterval: number;
    entryStep: number;
    otmvDurations: number[]; // Durations for which we have OTMV Values
    otmvDuration: number;
    occTrafficType: TrafficType;

    loading: boolean;
}

export class TvDetailsComparison extends PureComponent<TvDetailsComparisonProps, TvDetailsComparisonState> {
    // state = getInitialState(Utils.isExists(this.props.monitoredTv.trafficCounts.entryCounts) ? CountType.ENTRY : CountType.OCCUPANCY)

    state = {
        display: Utils.isExists(this.props.monitoredTvAfter.trafficCounts.entryCounts)
            ? CountType.ENTRY
            : CountType.OCCUPANCY,
        timeOver: null,
        entryInterval: Utils.isExists(this.props.monitoredTvAfter.trafficCounts.entryCounts)
            ? this.props.monitoredTvAfter.trafficCounts.entryCounts.timeRange.countDurationInMinutes
            : null,
        entryStep: Utils.isExists(this.props.monitoredTvAfter.trafficCounts.entryCounts)
            ? this.props.monitoredTvAfter.trafficCounts.entryCounts.timeRange.stepSizeInMinutes
            : null,
        otmvDurations: Utils.isExists(this.props.monitoredTvAfter.trafficCounts.occupancyCounts)
            ? this.props.monitoredTvAfter.trafficCounts.occupancyCounts.map(
                  item => item.timeRange.countDurationInMinutes,
              )
            : [],
        otmvDuration: Utils.isExists(this.props.monitoredTvAfter.trafficCounts.occupancyCounts)
            ? this.props.monitoredTvAfter.trafficCounts.occupancyCounts[0].timeRange.countDurationInMinutes
            : DEFAULT_OTMV_DURATION,
        occTrafficType: TrafficType.LOAD,
        loading: false,
    };

    handleTimeOverChange = (timeOver: Date) => {
        this.setState({ timeOver: timeOver });
    };
    setOtmvDuration = (duration: number) => {
        this.setState({ otmvDuration: duration });
    };
    getActivePeriods = memoizeOne((monitoredLoadValues: Array<MonitoredLoadValue>) => {
        return monitoredLoadValues && MonitoredLoadUtils.getActivePeriods(monitoredLoadValues);
    });

    renderEntryCounts = () => {
        const hourlyEntryCapacity =
            AppContext.getCapacityModel() === CapacityModel.WCM_NRP
                ? CapacityUtils.getWcmCapacity(
                      this.props.monitoredTvAfter.trafficCounts?.hourlyEntryCapacity,
                      this.props.monitoredTvAfter.trafficCounts?.entryCounts.timeRange,
                  )
                : this.props.monitoredTvAfter.trafficCounts?.hourlyEntryCapacity;

        return (
            <CountsGraph
                currentTime={this.props.currentTime}
                counts={this.props.monitoredTvAfter.trafficCounts.entryCounts.flightModelCounts}
                comparisonCounts={this.props.monitoredTvBefore.trafficCounts.entryCounts.flightModelCounts}
                activePeriods={this.getActivePeriods(this.props.monitoredTvAfter?.loadValues)}
                timeRange={this.props.monitoredTvAfter.trafficCounts?.entryCounts.timeRange}
                hourlyEntryCapacity={hourlyEntryCapacity}
                highlightedFlightsType={CountType.ENTRY}
                highlightedFlightsOccupancy={this.props.occupancyPeriods}
                timeOver={this.state.timeOver}
                onTimeOverChange={this.handleTimeOverChange}
            />
        );
    };
    renderEntryLoad = () => {
        const monitoredPeriod = this.props.monitoredTvAfter.trafficCounts?.entryCounts.timeRange;
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    E
                </Flex>
                <Flex direction="vertical">
                    <EntryLoadBar
                        title={'Before'}
                        loadValues={this.props.monitoredTvBefore?.loadValues}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.props.monitoredTvBefore?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                        showLoadInactivePeriods={true}
                    />
                    <EntryLoadBar
                        title={'After'}
                        loadValues={this.props.monitoredTvAfter?.loadValues}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.props.monitoredTvAfter?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                        showLoadInactivePeriods={true}
                    />
                </Flex>
            </Flex>
        );
    };
    renderEntry = () => {
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="20em">{this.renderEntryCounts()}</Flex>
                    <Flex size="1em">{this.renderEntryLoad()}</Flex>
                </Flex>
                <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                    <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5em' }}>
                        <ConditionalRender condition={this.state.loading}>
                            <LoadingOutlined style={{ fontSize: '2em' }} />
                        </ConditionalRender>
                        <SettingFilled style={{ fontSize: '2em' }} />
                    </div>
                    <Space direction="vertical">
                        <Select value={TrafficType.LOAD} disabled={true} style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                        </Select>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                Interval
                                <Select
                                    value={this.state.entryInterval}
                                    disabled={true}
                                    style={{ width: '100%' }}></Select>
                            </Col>
                            <Col span={12}>
                                Step
                                <Select value={this.state.entryStep} disabled={true} style={{ width: '100%' }}></Select>
                            </Col>
                        </Row>
                    </Space>
                </Flex>
            </Flex>
        );
    };
    renderOccupancyCounts = () => {
        return (
            <CountsGraph
                currentTime={this.props.currentTime}
                counts={
                    this.getOccupancyCounts(
                        this.props.monitoredTvAfter.trafficCounts.occupancyCounts,
                        this.state.otmvDuration,
                    )?.flightModelCounts
                }
                comparisonCounts={
                    this.getOccupancyCounts(
                        this.props.monitoredTvBefore.trafficCounts.occupancyCounts,
                        this.state.otmvDuration,
                    )?.flightModelCounts
                }
                activePeriods={this.getActivePeriods(this.props.monitoredTvAfter?.loadValues)}
                timeRange={
                    this.getOccupancyCounts(
                        this.props.monitoredTvAfter.trafficCounts.occupancyCounts,
                        this.state.otmvDuration,
                    )?.timeRange
                }
                otmvDefns={
                    this.props.monitoredTvAfter.trafficCounts.otmvDefns &&
                    this.props.monitoredTvAfter.trafficCounts.otmvDefns[this.state.otmvDuration]
                }
                timeOver={this.state.timeOver}
                onTimeOverChange={this.handleTimeOverChange}
            />
        );
    };

    private getOccupancyCounts = memoizeOne((counts: Array<TimeRangeCounts>, duration: number) => {
        if (counts) {
            const occCountsForDuration = counts.filter(item => item.timeRange.countDurationInMinutes === duration);
            if (occCountsForDuration?.length === 1) {
                return occCountsForDuration[0];
            }
        }
        return null;
    });

    private getOccupancyLoad = memoizeOne((loadValues: Array<MonitoredOccupancyLoadValues>, duration: number) => {
        if (loadValues) {
            const monitoredOccupancyLoadValues = loadValues.filter(item => item.durationInMin === duration);
            if (monitoredOccupancyLoadValues?.length === 1) {
                return monitoredOccupancyLoadValues[0];
            }
        }
        return null;
    });
    renderOccupancyLoad = () => {
        const occLoad =
            (this.getOccupancyLoad(this.props.monitoredTvAfter.occupancyLoadValues, this.state.otmvDuration) &&
                this.getOccupancyLoad(this.props.monitoredTvAfter.occupancyLoadValues, this.state.otmvDuration)
                    ?.occupancyLoadValues) ||
            [];
        const occLoadComparison =
            (this.getOccupancyLoad(this.props.monitoredTvBefore.occupancyLoadValues, this.state.otmvDuration) &&
                this.getOccupancyLoad(this.props.monitoredTvBefore.occupancyLoadValues, this.state.otmvDuration)
                    ?.occupancyLoadValues) ||
            [];
        const timeRange = this.getOccupancyCounts(
            this.props.monitoredTvBefore.trafficCounts.occupancyCounts,
            this.state.otmvDuration,
        )?.timeRange;
        const monitoredPeriod = { from: timeRange.from, to: timeRange.to };
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    O
                </Flex>
                <Flex direction="vertical">
                    <OccupancyLoadBar
                        title={'Before'}
                        loadValues={occLoadComparison}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.props.monitoredTvBefore?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                        showLoadInactivePeriods={true}
                    />
                    <OccupancyLoadBar
                        title={'After'}
                        loadValues={occLoad}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.props.monitoredTvAfter?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                        showLoadInactivePeriods={true}
                    />
                </Flex>
            </Flex>
        );
    };
    /**
     * Occupancy is not displayed until data could be retrieved.
     * If TvId refers to a refloc != AS, then there won't be occupancy counts returned
     */
    renderOccupancy = () => {
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="20em">{this.renderOccupancyCounts()}</Flex>
                    <Flex size="1em">{this.renderOccupancyLoad()}</Flex>
                </Flex>
                <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                    <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5em' }}>
                        <ConditionalRender condition={this.state.loading}>
                            <LoadingOutlined style={{ fontSize: '2em' }} />
                        </ConditionalRender>
                        <SettingFilled style={{ fontSize: '2em' }} />
                    </div>
                    <Space direction="vertical">
                        <Select value={TrafficType.LOAD} disabled style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                        </Select>
                        <Select
                            value={this.state.otmvDuration}
                            onChange={value => this.setOtmvDuration(value)}
                            style={{ width: '100%' }}>
                            {this.state.otmvDurations.map(duration => {
                                return (
                                    <Select.Option key={`duration-${duration}`} value={duration}>
                                        {duration} min
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Space>
                </Flex>
            </Flex>
        );
    };

    handleCountTypeChange = (value: CountType) => {
        this.setState({ display: value });
    };
    render() {
        return (
            <Flex direction="vertical" style={{ padding: '1.5em' }}>
                <div style={{ paddingLeft: '3em' }}>
                    <Radio.Group
                        value={this.state.display}
                        onChange={value =>
                            this.setState({ display: value.target.value }, () =>
                                this.handleCountTypeChange(value.target.value),
                            )
                        }>
                        <Radio.Button
                            style={{ width: '8em', textAlign: 'center' }}
                            disabled={!Utils.isExists(this.props.monitoredTvAfter.trafficCounts.entryCounts)}
                            value={CountType.ENTRY}>
                            Entry
                        </Radio.Button>
                        <Radio.Button
                            style={{ width: '8em', textAlign: 'center' }}
                            disabled={!Utils.isExists(this.props.monitoredTvAfter.trafficCounts.occupancyCounts)}
                            value={CountType.OCCUPANCY}>
                            Occupancy
                        </Radio.Button>
                    </Radio.Group>
                </div>
                {this.state.display === CountType.ENTRY && this.renderEntry()}
                {this.state.display === CountType.OCCUPANCY && this.renderOccupancy()}
            </Flex>
        );
    }
}
